import { useEffect, useRef } from "react";
import moment from "moment";
import { Badge, BadgeSize, BadgeVariant } from "shared/components";
import {
  Switch,
  SwitchSize,
  LabelPosition
} from "shared/components/forms/Switch";
import { type Register } from "shared/hooks";

interface WaitlistToggleProps {
  register: Register;
  occupied?: boolean | null;
  availableAt?: string | null;
  canChange: boolean;
}

// The Available-by date is a calendar date, so it counts as arrived on the day
// itself — matching the API's daily clearing job (`available_at <= now`).
const availableByArrived = (availableAt?: string | null): boolean => {
  if (!availableAt) return false;
  const date = moment(String(availableAt));
  return date.isValid() && !date.isAfter(moment(), "day");
};

export const WaitlistToggle = ({
  register,
  occupied,
  availableAt,
  canChange
}: WaitlistToggleProps) => {
  // Waitlist applies to any occupied unit whose Available-by date, if one is
  // set, hasn't arrived yet — the date itself is no longer required.
  const qualifies = Boolean(occupied) && !availableByArrived(availableAt);
  const value = Boolean(register("waitlist").value);
  const checked = qualifies && value;

  // Seeded with the qualifying state on mount so merely opening a unit never
  // flips the toggle — only a change the manager makes here does.
  const wasQualifying = useRef(qualifies);

  // The toggle turns itself off as soon as the unit stops qualifying — status
  // back to Available, or a set date arrived — writing the form value rather
  // than just the rendered state so a stale value isn't submitted on save.
  //
  // Auto-on covers every transition into qualifying, dated or not: an occupied
  // unit with no Available-by date has nothing to auto-clear against, so the
  // toggle stays on until a date is set and passes, the status goes back to
  // Available, or the manager turns it off. Turning it off keeps it off while
  // editing — `wasQualifying` only rearms on a real change of qualifying state,
  // so the effect never fights the manager by re-enabling on every render.
  useEffect(() => {
    const startedQualifying = qualifies && !wasQualifying.current;
    wasQualifying.current = qualifies;

    if (!canChange) return;
    if (startedQualifying && !value) {
      register("waitlist").onChange(true);
      return;
    }
    if (!qualifies && value) register("waitlist").onChange(false);
  }, [qualifies, value, canChange]);

  return (
    <div className="pt-4">
      <div className="flex items-center gap-2">
        <p className="text-sm font-bold text-content-1000">Waitlist</p>
        {checked ? (
          <Badge variant={BadgeVariant.SUCCESS} size={BadgeSize.SM}>
            Active now
          </Badge>
        ) : (
          <Badge
            variant={BadgeVariant.BASE}
            size={BadgeSize.SM}
            className="items-center"
            leftElement={
              <span className="size-1.5 shrink-0 self-center rounded-full bg-content-1000" />
            }
          >
            Inactive
          </Badge>
        )}
      </div>

      <div className="mt-2 flex items-start justify-between gap-6">
        <p className="max-w-[640px] text-sm text-content-300">
          While the unit is occupied, it stays listed as coming soon and
          prospects join the waitlist instead of booking tours.
        </p>
        <Switch
          size={SwitchSize.SM}
          position={LabelPosition.Right}
          checked={checked}
          disabled={!qualifies || !canChange}
          onChange={(value) => register("waitlist").onChange(value)}
        />
      </div>
    </div>
  );
};
